import { MultiSelect, RadioButton } from "@etframework/ui.components";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { PropertyRow } from "../PropertyUpdateRowInput";
import { useTranslation } from "react-i18next";

export const ArrayUpdateInput = forwardRef(
  (
    {
      change,
      field,
      settings,
      value = null,
      action,
      onRemove,
      getDataSource,
      id,
    },
    ref
  ) => {
    const { t } = useTranslation(["BulkUpdateTasksModal", "Shared"], {
      useSuspense: false,
    });

    const compRef = useRef(null);
    const removeRef = useRef(null);
    const [dataSource, setDataSource] = useState([]);
    const [dataFields, setDataFields] = useState({
      value: "value",
      text: "text",
    });

    const getDisplayValue = useCallback(
      (value) => {
        if (!value) return null;
        if (!dataSource) return null;

        return value.map((x) => {
          return (
            dataSource.find(
              (data) => data[dataFields.value] === x[dataFields.value]
            )?.[dataFields.text] ?? x[dataFields.text]
          );
        });
      },
      [dataSource, dataFields]
    );

    const onChangeRadioButtons = (e) => {
      change({ ...e, change: "action" });
    };

    const getAction = () => removeRef.current.getSelectedValue();

    const getValue = () => {
      const value = compRef.current?.getValue();
      return {
        field: field,
        action: getAction(),
        value: value,
        displayValue: getDisplayValue(value),
        hasValue: Array.isArray(value) && value.length > 0,
        settings: settings,
      };
    };

    useImperativeHandle(
      ref,
      () => ({
        getValue,
        focus: () => compRef.current?.getComponentRef()?.focusIn(),
        applyValue: (properties) => {
          const value = compRef.current.getValue();
          if (Array.isArray(value) && value.length > 0)
            properties[`${field}_${getAction()}`] = value;
        },
      }),
      [dataSource]
    );

    useEffect(() => {
      const _getData = async () => {
        const result = await getDataSource(field);
        if (result?.data) setDataSource(result?.data);
        if (result.fields) setDataFields(result?.fields);
      };

      _getData();
    }, []);

    return (
      <PropertyRow
        key={field}
        field={field}
        label={settings?.headerText}
        onRemove={onRemove}
        settings={settings}
        content={
          <div className="w-100">
            <MultiSelect
              ref={compRef}
              id={id}
              fields={dataFields}
              allowCustomValue={settings?.allowCustomValue}
              dataSource={dataSource}
              allowObjectBinding
              value={value}
              change={(e) => change({ ...e, type: "value" })}
            />
          </div>
        }
        footer={
          <div className="multiselect-footer-options">
            <RadioButton
              id={id + "_add-button"}
              name={id}
              checked={action === "Add"}
              jsxElement={
                <span className="fw-normal">{t("UpdateEditor.Add")}</span>
              }
              value={"Add"}
              change={onChangeRadioButtons}
            />
            <RadioButton
              id={id + "_remove-button"}
              ref={removeRef}
              checked={action === "Remove"}
              name={id}
              jsxElement={
                <span className="fw-normal">{t("UpdateEditor.Remove")}</span>
              }
              value={"Remove"}
              change={onChangeRadioButtons}
            />
          </div>
        }
      />
    );
  }
);
